import React from "react";
import { ChevronRight } from "lucide-react";
import { Pill } from "./Pill";
import { SourceBadge } from "./SourceBadge";
import { useLookups } from "../lib/lookups";
import { formatDayMonIST } from "../lib/api";

const G = "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export function LeadCard({ lead, onClick, onDragStart, onAdvance, nextStage, quickActions, overdue }) {
  const { CATEGORY_COLORS } = useLookups();
  const dateBadge = formatDayMonIST(lead.created_at);
  const hasAction = !!(quickActions && quickActions.length > 0) || !!nextStage;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart && onDragStart(e, lead)}
      onClick={() => onClick && onClick(lead)}
      data-testid={`lead-card-${lead.id}`}
      className="card card-hover relative p-4 cursor-grab active:cursor-grabbing"
    >
      {/* Avatar + name row */}
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-[800] flex-shrink-0 select-none"
          style={{ background: G }}
        >
          {initials(lead.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[700] text-[13px] text-[#0D0B1F] truncate leading-tight pr-4">{lead.name}</div>
          {lead.course_interest && (
            <div className="text-[11px] text-[#8B90A0] font-[500] mt-0.5 truncate">{lead.course_interest}</div>
          )}
        </div>
        {overdue && <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0 mt-1" data-testid="overdue-dot" />}
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        <Pill color={CATEGORY_COLORS[lead.category]}>{lead.category}</Pill>
        <SourceBadge source={lead.source} />
      </div>

      {lead.summary && (
        <div className="text-[11px] text-[#8B90A0] mt-2 line-clamp-2 leading-relaxed">{lead.summary}</div>
      )}

      {/* Stage action */}
      {hasAction && (
        <div className="mt-3 pr-14">
          {quickActions && quickActions.length > 0 ? (
            <div className="flex gap-1.5">
              {quickActions.map((qa) => (
                <button
                  key={qa.stage}
                  onClick={(e) => { e.stopPropagation(); onAdvance && onAdvance(lead, qa.stage); }}
                  data-testid={`quick-${qa.stage.toLowerCase().replace(/\s+/g, "-")}-${lead.id}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-[7px] text-[11px] font-[600] transition-colors ${
                    qa.variant === "win"
                      ? "bg-[#166534] text-white hover:bg-[#14532d]"
                      : "border border-[#7f1d1d] text-[#7f1d1d] hover:bg-[#7f1d1d] hover:text-white"
                  }`}
                >
                  {qa.label}
                </button>
              ))}
            </div>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onAdvance && onAdvance(lead, nextStage); }}
              data-testid={`advance-stage-${lead.id}`}
              title={`Move to ${nextStage}`}
              className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-[7px] text-[11px] font-[600] transition-colors"
              style={{ backgroundColor: "#EEE9FF", color: "#6C47FF" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#6C47FF"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#EEE9FF"; e.currentTarget.style.color = "#6C47FF"; }}
            >
              <span className="truncate">→ {nextStage}</span>
              <ChevronRight className="h-3 w-3 flex-shrink-0" />
            </button>
          )}
        </div>
      )}

      {/* Date badge */}
      {dateBadge && (
        <span
          data-testid={`lead-date-badge-${lead.id}`}
          className="absolute pointer-events-none font-[700]"
          style={{
            bottom: "8px", right: "8px",
            background: G,
            color: "#fff",
            borderRadius: "5px",
            fontSize: "10px",
            padding: "2px 6px",
            lineHeight: "1.4",
          }}
        >
          {dateBadge}
        </span>
      )}
    </div>
  );
}
